import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';

import CampaignsAx    from 'app/actions/company-admin/campaigns';
import Icon           from 'app/components/common/icon';
import Modal          from 'app/components/common/modal';
import CalendarPicker from 'app/components/company-admin/integrations/calendar-picker';
import IntsDuck       from 'app/ducks/company-admin/integrations';
import EventsDuck     from 'app/ducks/company-admin/vol-events';
import prompts        from 'app/prompts';
import AuthSlx        from 'app/selectors/auth';

const logoMap = {
  google: '/images/logos/google-calendar-256.png',
  microsoft: '/images/logos/microsoft-outlook-256.png',
};

const entitySettingsMap = {
  VolEvent: {name: 'event',    saveFnName: 'saveEvent',    syncFnName: 'syncEvent'},
  Campaign: {name: 'campaign', saveFnName: 'saveCampaign', syncFnName: 'syncCampaign'},
};

class ModalCadminCalendarSettings extends React.PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      selectedCalendarId: null,
      savePending: false,
      syncPending: false,
    };

    this.onClose = this.onClose.bind(this);
    this.onClickRemove = this.onClickRemove.bind(this);
    this.onClickSave = this.onClickSave.bind(this);
    this.onClickSync = this.onClickSync.bind(this);
    this.onSelectCal = this.onSelectCal.bind(this);
  }

  componentDidMount() {
    this.props.loadCals();
  }

  get entitySettings() {
    return entitySettingsMap[this.props.entityType];
  }

  onClose() {
    this.props.onClose();
  }

  async onClickRemove() {
    const didConfirm = await prompts.confirm(`Remove this ${this.entitySettings.name} from the connected calendar?`);
    if (didConfirm) {
      this.save(null);
    }
  }

  async onClickSync() {
    const syncFn = this.props[this.entitySettings.syncFnName];
    this.setState({syncPending: true});
    await syncFn();
    this.setState({syncPending: false});
  }

  onClickSave() {
    this.save(this.state.selectedCalendarId);
  }

  async save(calendarId) {
    const saveFn = this.props[this.entitySettings.saveFnName];
    this.setState({savePending: true});
    await saveFn(calendarId);
    this.setState({savePending: false}, () => {
      this.onClose();
    });
  }

  onSelectCal(selectedCalendarId) {
    this.setState({selectedCalendarId});
  }

  renderContents() {
    const {grants, allCals, entity, isBsSuper} = this.props;
    const {savePending, syncPending} = this.state;
    const isLoading = !grants || !allCals;
    if (isLoading) return 'loading...';

    const connectedCal = allCals.find(cal => cal.id === entity.nylasCalendarId);

    const btnDisabled = (() => {
      if (savePending) return true;
      if (!this.state.selectedCalendarId) return true;
      return false;
    })();
    const btnLabel = savePending ? 'Saving...' : 'Save Changes';

    const calendarChip = (() => {
      if (!connectedCal) return null;
      if (connectedCal.deleted) return <span className="chip small danger">Deleted</span>;
      if (connectedCal.readOnly) return <span className="chip small danger">Read Only</span>;
      return null;
    })();
    const hasGoodCal = !!(connectedCal && !calendarChip);
    const showSyncBtn = !!(isBsSuper && hasGoodCal);

    return (<>
      <label className="bform-h3">Integration</label>
      <div className="_flex _col _gap-24 _justify-start _align-start">
        {grants.map((grant) => {
          const imgSrc = logoMap[grant.provider] || null;
          // const isValid = false;
          const isValid = grant.status === 'valid';
          return (
            <div key={grant.id}>
              <div className="_flex _gap-6 _justify-start _align-center">
                {!!imgSrc && <img src={imgSrc} style={{width: 24, display: 'inline-block'}} />}
                <span>{grant.email}</span>
                {isValid
                  ? <span className="chip green small">Connected</span>
                  : <span className="chip danger small">Disconnected</span>
                }
              </div>
              {!isValid && (
                <div className="_italic">Please reconnect this integration in Settings &rarr; Integrations &rarr; Calendar</div>
              )}
            </div>
          );
        })}
      </div>

      <label className="bform-h3">Connected Calendar</label>
      {connectedCal ? (<>
        <div className="_flex _align-center _justify-start _gap-6">{connectedCal.title} {calendarChip}</div>
        <button className="btn danger secondary small _mt-12" onClick={this.onClickRemove} disabled={savePending}>{savePending ? 'Saving...' : 'Remove from calendar'}</button>
      </>) : (<>
        <CalendarPicker value={this.state.selectedCalendarId} onChange={this.onSelectCal} />
      </>)}

      {/* <pre>{JSON.stringify({calendars, grants, volEvent},null,4)}</pre> */}

      <div className="bform-actions">
        {/* {!!connectedCal && ( */}
        {/*   <button className="btn danger secondary" onClick={this.onClickRemove}>Remove from calendar integration</button> */}
        {/* )} */}
        {showSyncBtn && (
          <button className="btn icon secondary" onClick={this.onClickSync} disabled={syncPending}><Icon.ShowTheater /> {syncPending ? 'Syncing...' : 'Force Sync'}</button>
        )}
        <div className="bform-actions-right">
          <button className="btn blue" onClick={this.onClickSave} disabled={btnDisabled}>{btnLabel}</button>
        </div>
      </div>
    </>);
  }

  render() {
    const {entity} = this.props;
    if (!entity) return null;

    return (
      <Modal className="bform" onClose={this.onClose}>
        <h1 className="bform-h1">Calendar Settings</h1>

        <label className="bform-h3">{this.entitySettings.name}</label>
        <div>{entity.title || entity.name || ''}</div>

        {/* <pre>{JSON.stringify({allCals: this.props.allCals, validCals: this.props.validCals}, null, 4)}</pre> */}

        {this.renderContents()}
      </Modal>
    );
  }

}

ModalCadminCalendarSettings.propTypes = {
  onClose: PropTypes.func.isRequired,
  entity: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    title: PropTypes.string,
    nylasCalendarId: PropTypes.string,
  }).isRequired,
  entityType: PropTypes.oneOf(['VolEvent', 'Campaign']).isRequired,
};
ModalCadminCalendarSettings.defaultProps = {};

const stateToProps = (state) => ({
  grants: IntsDuck.Slx.nylasGrants(state),
  allCals: IntsDuck.Slx.allCalendars(state),
  // validCals: IntsDuck.Slx.validCalendars(state),
  // updatePending: EventsDuck.Slx.calendarUpdatePending(state),
  isBsSuper: AuthSlx.isBsSuper(state),
});

const dispatchToProps = (dispatch, ownProps) => ({
  loadCals: () => dispatch(IntsDuck.Ax.loadCalendars()),
  saveEvent:    (nylasCalendarId) => dispatch(EventsDuck.Ax.updateCalendar(undefined, ownProps.entity.id, nylasCalendarId)),
  saveCampaign: (nylasCalendarId) => dispatch(CampaignsAx.updateCalendar(  undefined, ownProps.entity.id, nylasCalendarId)),
  syncEvent:    () => dispatch(EventsDuck.Ax.forceSyncCalendar(undefined, ownProps.entity.id)),
  syncCampaign: () => dispatch(CampaignsAx.forceSyncCalendar(  undefined, ownProps.entity.id)),
});

export default connect(stateToProps, dispatchToProps)(ModalCadminCalendarSettings);
